import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, UserPlus, Users } from "lucide-react";
import { api } from "./api";
import { Modale } from "./ui";

/* Remplacer un prof (école, 30.10.2026) : un assistant pas à pas, comme la fusion de
   cours. Qui part et quels cours → à partir de quand → par qui → récapitulatif →
   « Confirmer le remplacement ». Planning, paie, app, page Professeurs et e-mails aux
   familles suivent (voir console/remplacements.py). */

const ETAPES = ["Qui part ?", "À partir de quand ?", "Par qui ?", "Récapitulatif"];

/* Une photo de téléphone pèse plusieurs Mo : réduite à 900 px avant l'envoi. */
const reduirePhoto = (fichier) => new Promise((resoudre, refuser) => {
  const lecteur = new FileReader();
  lecteur.onerror = () => refuser(new Error("Photo illisible."));
  lecteur.onload = () => {
    const img = new Image();
    img.onerror = () => refuser(new Error("Photo illisible."));
    img.onload = () => {
      const echelle = Math.min(1, 900 / Math.max(img.width, img.height));
      const toile = document.createElement("canvas");
      toile.width = Math.round(img.width * echelle);
      toile.height = Math.round(img.height * echelle);
      toile.getContext("2d").drawImage(img, 0, 0, toile.width, toile.height);
      resoudre(toile.toDataURL("image/jpeg", 0.85));
    };
    img.src = lecteur.result;
  };
  lecteur.readAsDataURL(fichier);
});

const lundiProchain = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  // La date locale (toISOString donnait la date UTC : après minuit, le dimanche).
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const lisible = (iso) => (iso ? iso.split("-").reverse().join(".") : "");

export default function RemplacerProf({ profs, onFermer, onFait }) {
  const [etape, setEtape] = useState(0);
  const [ancien, setAncien] = useState("");
  const [infos, setInfos] = useState(null);
  const [cours, setCours] = useState([]);
  const [desLe, setDesLe] = useState(lundiProchain());
  const [qui, setQui] = useState("nouveau");
  const [existant, setExistant] = useState("");
  const [nouveau, setNouveau] = useState({ prenom: "", nom_complet: "", email: "", role: "", bio: "", photo: "", tarif_fixe: "", tarif_fixe_par: "heure" });
  const [familles, setFamilles] = useState(true);
  const [message, setMessage] = useState("");
  const [recap, setRecap] = useState(null);
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!ancien) { setInfos(null); return; }
    setErreur("");
    api(`/remplacements/prof/${encodeURIComponent(ancien)}`)
      .then((d) => { setInfos(d); setCours(d.cours.map((c) => c.id)); })
      .catch((e) => setErreur(e.message));
  }, [ancien]);

  const corps = () => ({
    ancien_prof_id: ancien, cours_ids: cours, des_le: desLe,
    ...(qui === "equipe" ? { nouveau_prof_id: existant }
      : { nouveau: { ...nouveau, tarif_fixe: nouveau.tarif_fixe ? Number(nouveau.tarif_fixe) : null } }),
    prevenir_familles: familles, message_familles: message,
  });
  const verifier = async () => {
    setErreur(""); setEnvoi(true);
    try { setRecap(await api("/remplacements/apercu", { method: "POST", body: JSON.stringify(corps()) })); setEtape(3); }
    catch (e) { setErreur(e.message); }
    setEnvoi(false);
  };
  const confirmer = async () => {
    setErreur(""); setEnvoi(true);
    try {
      const r = await api("/remplacements", { method: "POST", body: JSON.stringify(corps()) });
      onFait(r.message);
    } catch (e) { setErreur(e.message); setEnvoi(false); }
  };
  const photo = async (fichier) => {
    if (!fichier) return;
    try { setNouveau({ ...nouveau, photo: await reduirePhoto(fichier) }); } catch (e) { setErreur(e.message); }
  };
  const champ = (cle, label, props = {}) => (
    <label className="block text-xs text-[var(--muted)]">{label}
      <input className="input-field w-full mt-1" value={nouveau[cle]} onChange={(e) => setNouveau({ ...nouveau, [cle]: e.target.value })} {...props} />
    </label>
  );
  const nomAncien = infos?.prof?.prenom || "";
  const pret = [
    !!ancien && cours.length > 0,
    !!desLe,
    qui === "equipe" ? !!existant : !!(nouveau.prenom.trim() && nouveau.email.trim()),
  ];

  return (
    <Modale titre="Remplacer un prof" sous_titre={`Étape ${etape + 1} sur 4 — ${ETAPES[etape]}`} onFermer={onFermer} large testid="remplacer-prof">
      <div className="flex gap-1 mb-5">
        {ETAPES.map((e, i) => <div key={e} className="h-1.5 flex-1 rounded-full" style={{ background: i <= etape ? "var(--primary)" : "#E2E8F0" }} />)}
      </div>

      {etape === 0 && (
        <div className="space-y-4">
          <label className="block text-sm font-medium">Quel prof part ?
            <select className="input-field w-full mt-1" value={ancien} onChange={(e) => setAncien(e.target.value)} data-testid="remplacer-ancien">
              <option value="">— choisir —</option>
              {profs.filter((p) => p.actif !== false).map((p) => <option key={p.id} value={p.id}>{p.prenom}{p.nom_complet && p.nom_complet !== p.prenom ? ` (${p.nom_complet})` : ""}</option>)}
            </select>
          </label>
          {infos && (
            <div>
              <div className="text-sm font-medium mb-2">Ses cours à remplacer</div>
              {!infos.cours.length ? <p className="text-sm text-[var(--muted)]">Aucun cours actif au planning.</p> : infos.cours.map((c) => (
                <label key={c.id} className="flex items-start gap-2 text-sm py-1.5">
                  <input type="checkbox" className="mt-1" checked={cours.includes(c.id)}
                    onChange={(e) => setCours(e.target.checked ? [...cours, c.id] : cours.filter((x) => x !== c.id))} />
                  <span><strong>{c.nom}</strong> · {c.jour} {c.debut}–{c.fin}
                    <span className="text-[var(--muted)]"> · {c.familles} famille(s){c.partage ? ` · avec ${c.professeur}` : ""}</span></span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {etape === 1 && (
        <div className="space-y-3">
          <label className="block text-sm font-medium">Le remplaçant commence le
            <input type="date" className="input-field w-full mt-1" value={desLe} onChange={(e) => setDesLe(e.target.value)} data-testid="remplacer-date" />
          </label>
          <p className="text-sm text-[var(--muted)]">
            Les séances d'avant le {lisible(desLe)} restent payées à {nomAncien} ; à partir de cette date, au remplaçant.{" "}
            {nomAncien} n'a plus accès à son application à partir de cette date (s'il ou elle ne donne plus d'autre cours).
          </p>
        </div>
      )}

      {etape === 2 && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <button className={qui === "nouveau" ? "btn-primary text-sm" : "btn-ghost text-sm"} onClick={() => setQui("nouveau")}><UserPlus size={14} /> Un nouveau prof</button>
            <button className={qui === "equipe" ? "btn-primary text-sm" : "btn-ghost text-sm"} onClick={() => setQui("equipe")}><Users size={14} /> Un prof de l'équipe</button>
          </div>
          {qui === "equipe" ? (
            <select className="input-field w-full" value={existant} onChange={(e) => setExistant(e.target.value)} data-testid="remplacer-existant">
              <option value="">— choisir —</option>
              {(infos?.equipe || []).map((p) => <option key={p.id} value={p.id}>{p.prenom}</option>)}
            </select>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="remplacer-nouveau">
              {champ("prenom", "Prénom (comme au planning)")}
              {champ("nom_complet", "Nom complet")}
              {champ("email", "E-mail (connexion à son application)", { type: "email" })}
              {champ("role", "Disciplines (page Professeurs)", { placeholder: "Salsa · Bachata" })}
              <label className="block text-xs text-[var(--muted)] sm:col-span-2">Texte de présentation pour le site
                <textarea className="input-field w-full mt-1" rows={3} value={nouveau.bio} onChange={(e) => setNouveau({ ...nouveau, bio: e.target.value })} />
              </label>
              <label className="block text-xs text-[var(--muted)]">Photo pour le site
                <input type="file" accept="image/*" className="block mt-1 text-xs" onChange={(e) => photo(e.target.files?.[0])} />
                {nouveau.photo && <img src={nouveau.photo} alt="" className="mt-2 h-24 rounded-lg object-cover" />}
              </label>
              <div className="text-xs text-[var(--muted)]">Tarif
                <div className="flex gap-2 mt-1">
                  <input className="input-field w-24" type="number" min="0" placeholder="barème" value={nouveau.tarif_fixe}
                    onChange={(e) => setNouveau({ ...nouveau, tarif_fixe: e.target.value })} />
                  {nouveau.tarif_fixe && (
                    <select className="input-field" value={nouveau.tarif_fixe_par} onChange={(e) => setNouveau({ ...nouveau, tarif_fixe_par: e.target.value })}>
                      <option value="heure">/ heure</option><option value="cours">/ cours</option>
                    </select>
                  )}
                </div>
                <span className="block mt-1">Vide = barème selon le nombre d'élèves.</span>
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={familles} onChange={(e) => setFamilles(e.target.checked)} /> Prévenir les familles de ces cours par e-mail
          </label>
          {familles && (
            <textarea className="input-field w-full text-sm" rows={2} placeholder="Un mot de l'école pour les familles (facultatif)"
              value={message} onChange={(e) => setMessage(e.target.value)} />
          )}
        </div>
      )}

      {etape === 3 && recap && (
        <div className="space-y-3" data-testid="remplacer-recap">
          <ul className="space-y-1.5 text-sm">
            {recap.points.map((p) => <li key={p} className="flex gap-2"><Check size={14} className="shrink-0 mt-0.5" style={{ color: "#15803D" }} /> {p}</li>)}
          </ul>
          <div className="rounded-xl border p-3 text-sm" style={{ borderColor: "var(--border)" }}>
            {recap.cours.map((c) => <div key={c.id}>{c.nom} ({c.quand}) : <s className="text-[var(--muted)]">{c.avant}</s> → <strong>{c.apres}</strong></div>)}
          </div>
        </div>
      )}

      {erreur && <p className="text-sm mt-4 rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2" }}>{erreur}</p>}

      <div className="flex justify-between gap-2 mt-6">
        <button className="btn-ghost text-sm" onClick={() => (etape ? setEtape(etape - 1) : onFermer())}>
          <ArrowLeft size={14} /> {etape ? "Retour" : "Annuler"}
        </button>
        {etape < 2 && <button className="btn-primary text-sm" disabled={!pret[etape]} onClick={() => setEtape(etape + 1)} data-testid="remplacer-suivant">Suivant <ArrowRight size={14} /></button>}
        {etape === 2 && <button className="btn-primary text-sm" disabled={!pret[2] || envoi} onClick={verifier} data-testid="remplacer-verifier">Voir le récapitulatif <ArrowRight size={14} /></button>}
        {etape === 3 && <button className="btn-primary text-sm" disabled={envoi} onClick={confirmer} data-testid="remplacer-confirmer"><Check size={14} /> Confirmer le remplacement</button>}
      </div>
    </Modale>
  );
}
